import { createContext, use, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useCopilotSessionRegistry, CopilotProvider, useCopilotInstruction } from '@customerio/hydra-copilot/react';
import type { RegistryCopilotSession, RegistryEntry } from '@customerio/hydra-copilot';

import { useCopilotSessionAutoName } from '../hooks/use-copilot-session-auto-name';
import { useCopilotSessionUrlSync } from '../hooks/use-copilot-session-url-sync';
import { useCopilotSessionRefreshOnFocus } from '../hooks/use-copilot-session-refresh-on-focus';
import { useMasqAccessLevel } from '../hooks/use-masq-access-level';
import { useTrackCopilotWorkspace } from '../workspace-context';
import {
  copilotLibrary,
  copilotLibraryWithQueryBuilder,
  copilotPromptOptions,
  copilotPromptOptionsWithQueryBuilder,
} from './openui-library';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { CopilotMermaidDiagram } from './primitives/mermaid-diagram';
import { CopilotSessionContext } from './session-context';

interface CopilotChatInputValue {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
}

interface CopilotConversationsValue {
  newConversation: () => RegistryCopilotSession;
  switchToConversation: (serverId: string) => RegistryCopilotSession;
  removeConversation: (serverId: string) => void;
  entries: ReadonlyArray<RegistryEntry>;
}

// Split so session-only consumers don't re-render on every keystroke.
const CopilotChatInputContext = createContext<CopilotChatInputValue | null>(null);
const CopilotConversationsContext = createContext<CopilotConversationsValue | null>(null);

export function useCopilotSessionContext() {
  const session = use(CopilotSessionContext);
  if (session == null) {
    throw new Error('useCopilotSessionContext must be used within CopilotSessionProvider');
  }
  return session;
}

export function useCopilotConversations() {
  const conversations = use(CopilotConversationsContext);
  if (conversations == null) {
    throw new Error('useCopilotConversations must be used within CopilotSessionProvider');
  }
  return conversations;
}

export function useCopilotChatContext() {
  const session = use(CopilotSessionContext);
  const input = use(CopilotChatInputContext);
  if (session == null || input == null) {
    throw new Error('useCopilotChatContext must be used within CopilotSessionProvider');
  }
  return { session, ...input };
}

export { useMasqAccessLevel } from '../hooks/use-masq-access-level';

type CopilotSessionProviderProps = { children: ReactNode };

export function CopilotSessionProvider({ children }: CopilotSessionProviderProps) {
  const { registry, activeSession: session, entries } = useCopilotSessionRegistry();
  const [input, setInput] = useState('');
  const { isMasq, isMasqWithoutAccess } = useMasqAccessLevel();

  useCopilotSessionAutoName(session);
  useCopilotSessionUrlSync(session, setInput);
  useCopilotSessionRefreshOnFocus(session);
  useTrackCopilotWorkspace();

  useCopilotInstruction({
    instruction: `The current user is masquerading into this account but does NOT have masquerade access permissions.
You will NOT be able to read or modify any account data — all API calls and tool invocations that access this account's data will fail with a 403 forbidden error.
You can still answer general questions about Customer.io, help with strategy, explain concepts, or assist with tasks that don't require accessing this specific account's data.
If the user asks you to do something that requires account data access, explain that their masquerade session does not have sufficient permissions and suggest they request masquerade access from an admin.`,
    isEnabled: isMasqWithoutAccess,
  });

  useCopilotInstruction({
    instruction: `The current user is masquerading into this account with read-only access.
You can read account data but you cannot modify it — any API calls or tool invocations that attempt to write, update, or delete account data will fail with a 403 forbidden error.
If the user asks you to make changes, explain that their masquerade session has read-only access and suggest they log in directly as an admin if changes are needed.`,
    isEnabled: isMasq && !isMasqWithoutAccess,
  });

  const inputValue: CopilotChatInputValue = useMemo(() => ({ input, setInput }), [input, setInput]);

  const isQueryBuilderEnabled = useFeatureFlag(AccountFeatureFlag.AgentQueryBuilder);

  const conversationsValue: CopilotConversationsValue = useMemo(
    () => ({
      newConversation: () => registry.startNew(),
      switchToConversation: (serverId: string) => registry.activateByServerId(serverId),
      removeConversation: (serverId: string) => {
        registry.removeByServerId(serverId);
      },
      entries,
    }),
    [registry, entries],
  );

  return (
    <CopilotSessionContext.Provider value={session}>
      <CopilotConversationsContext.Provider value={conversationsValue}>
        <CopilotChatInputContext.Provider value={inputValue}>
          <CopilotProvider
            library={isQueryBuilderEnabled ? copilotLibraryWithQueryBuilder : copilotLibrary}
            libraryPromptOptions={isQueryBuilderEnabled ? copilotPromptOptionsWithQueryBuilder : copilotPromptOptions}
            mermaidComponent={CopilotMermaidDiagram}
          >
            {children}
          </CopilotProvider>
        </CopilotChatInputContext.Provider>
      </CopilotConversationsContext.Provider>
    </CopilotSessionContext.Provider>
  );
}
